import { useState, useEffect } from "react";
import { Brain, X, Tag } from "@phosphor-icons/react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Input } from "./ui/input";
import * as API from "../lib/api";
import { toast } from "sonner";

/**
 * Save-as-Memory dialog. Non-blocking — caller is not awaited; this dialog
 * fires the API call, shows a toast, and closes. Failures show a toast but
 * never throw to caller.
 */
export default function SaveMemoryDialog({ open, onOpenChange, sourceContent, onSaved }) {
  const [content, setContent] = useState(sourceContent || "");
  const [tagInput, setTagInput] = useState("");
  const [tags, setTags] = useState([]);
  const [saving, setSaving] = useState(false);

  // Sync pre-filled content when the dialog opens with a new source
  useEffect(() => {
    if (open) {
      setContent(sourceContent || "");
    }
  }, [open, sourceContent]);

  const reset = () => {
    setContent("");
    setTagInput("");
    setTags([]);
  };

  const addTag = () => {
    const t = tagInput.trim().toLowerCase();
    if (!t) return;
    if (tags.includes(t)) {
      setTagInput("");
      return;
    }
    setTags((prev) => [...prev, t]);
    setTagInput("");
  };

  const removeTag = (t) => setTags((prev) => prev.filter((x) => x !== t));

  const save = async () => {
    const trimmed = content.trim();
    if (!trimmed) {
      toast.error("Memory content empty");
      return;
    }
    setSaving(true);
    try {
      const mem = await API.createMemory(trimmed, tags, "chat");
      toast.success("Saved to memory", {
        description: tags.length ? `Tags: ${tags.join(", ")}` : undefined,
      });
      onSaved?.(mem);
      reset();
      onOpenChange(false);
    } catch (e) {
      toast.error("Failed to save memory");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) reset();
        onOpenChange(v);
      }}
    >
      <DialogContent
        className="bg-[#0f0f0f] border-white/10 text-white max-w-md"
        data-testid="save-memory-dialog"
      >
        <DialogHeader>
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center">
              <Brain size={18} weight="duotone" className="text-cyan-400" />
            </div>
            <div>
              <DialogTitle className="font-display tracking-tight">
                Save as memory
              </DialogTitle>
              <DialogDescription className="text-white/55 text-xs">
                Journey will remember this in every conversation.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-3">
          <Textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="What should Journey remember?"
            className="bg-transparent border-white/10 focus-visible:ring-cyan-500/40 min-h-[110px] text-[14.5px]"
            data-testid="save-memory-content"
          />

          <div>
            <label className="text-xs text-white/55 mb-1.5 flex items-center gap-1.5">
              <Tag size={12} weight="duotone" />
              Tags (optional)
            </label>
            <div className="flex gap-2">
              <Input
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === ",") {
                    e.preventDefault();
                    addTag();
                  }
                }}
                placeholder="e.g. work, idea, recipe"
                className="bg-transparent border-white/10 focus-visible:ring-cyan-500/40"
                data-testid="save-memory-tag-input"
              />
              <Button
                type="button"
                variant="outline"
                onClick={addTag}
                className="border-white/10 hover:bg-white/5 shrink-0"
                data-testid="save-memory-tag-add"
              >
                Add
              </Button>
            </div>
            {tags.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {tags.map((t) => (
                  <span
                    key={t}
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-cyan-500/10 border border-cyan-500/25 text-xs text-cyan-300"
                    data-testid={`save-memory-tag-${t}`}
                  >
                    {t}
                    <button
                      onClick={() => removeTag(t)}
                      className="text-cyan-300/70 hover:text-cyan-100"
                      aria-label={`Remove ${t}`}
                    >
                      <X size={10} weight="bold" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-1">
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            className="text-white/70"
            data-testid="save-memory-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={save}
            disabled={saving || !content.trim()}
            className="bg-cyan-500 hover:bg-cyan-400 text-black font-medium rounded-xl"
            data-testid="save-memory-save"
          >
            Save memory
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
